import { DropdownOption, DropdownValue, LocalizedDropdownOptions } from '../../../../@types/types'
import camelCase from 'camelcase'
import { INVALID, VALID } from '../../../../services/domain/dropdownSelect'
import { FormStateFeatureState } from '../../../../@types/state-types'
import React, { CSSProperties } from 'react'
import { TFunction } from 'i18next'

export const addNoInformationOption = (
  dropdownOptions: DropdownOption[],
  noInformation: string | undefined,
  isFieldMandatory: boolean,
  t: TFunction
): DropdownOption[] =>
  noInformation
    ? [buildNoInformationDropDownOption(noInformation, isFieldMandatory, t)].concat(
        dropdownOptions.filter((option) => option.value !== noInformation)
      )
    : dropdownOptions

export const buildNoInformationDropDownOption = (
  key: string,
  isFieldMandatory: boolean,
  t: TFunction
): DropdownOption => {
  const fieldMandatoryLabel = isFieldMandatory
    ? t('dropdown.noInformation.mandatory', key)
    : t('dropdown.noInformation.optional', key)
  return {
    value: key,
    label: fieldMandatoryLabel,
  }
}

export const mapLocalizedDropdownOptionsToDropdownOptions = (
  localizedDropdownOptions: LocalizedDropdownOptions,
  noInformation: string | undefined,
  t: TFunction
): DropdownOption[] => {
  const { translationKeyGroup, options: optionsKeys } = localizedDropdownOptions

  return optionsKeys.map((key) => {
    if (key === noInformation) {
      return { value: key, label: undefined }
    } else {
      return {
        value: key,
        label: t(`${translationKeyGroup}.${camelCase(key)}`, key),
      }
    }
  })
}

export function buildDropdownOptions(
  isFieldMandatory: boolean,
  options: DropdownOption[] | LocalizedDropdownOptions,
  t: TFunction,
  noInformation?: string
) {
  if (Array.isArray(options)) {
    return addNoInformationOption(options, noInformation, isFieldMandatory, t)
  } else {
    return addNoInformationOption(
      mapLocalizedDropdownOptionsToDropdownOptions(options as LocalizedDropdownOptions, noInformation, t),
      noInformation,
      isFieldMandatory,
      t
    )
  }
}

export const findInDropdownOptions = (dropdownOptions: DropdownOption[], newValue: any) =>
  dropdownOptions.find((option) => JSON.stringify(option.value) === newValue)!

export function getSelected(dropdownOptions: DropdownOption[], newValue: any) {
  const isEmptyOrPlaceholder = !newValue || newValue === VALID || newValue === INVALID
  return isEmptyOrPlaceholder ? { value: null, label: null } : findInDropdownOptions(dropdownOptions, newValue)
}

export function handleEmpty(value: any): DropdownValue {
  return !value ? 'NO_INFORMATION' : value
}

export function formHasErrors(errors: string[] | undefined, formState: FormStateFeatureState, name: string) {
  return () => errors && errors.length > 0 && (formState.submitCount > 0 || formState.touched[name])
}

export function isValueInvalid(
  value: DropdownValue[] | undefined,
  highlightValidation: boolean,
  allowEmpty: boolean | undefined,
  hasErrors: () => false | boolean | undefined
) {
  const isValueEmpty = !value || value.length === 0
  return (isValueEmpty && highlightValidation && !allowEmpty) || hasErrors()
}

export function getNewStyles(
  style: React.CSSProperties | undefined,
  highlightValidation: boolean,
  value: DropdownValue[] | undefined,
  isInvalid: undefined | boolean
) {
  let newStyles: CSSProperties = style ?? {}
  if (highlightValidation && value !== null && !isInvalid) {
    newStyles = { ...style, borderColor: 'var(--color-border-success)' }
  } else if (isInvalid) {
    newStyles = { ...style, borderColor: undefined }
  }
  newStyles = { ...newStyles, textOverflow: 'ellipsis' }
  return newStyles
}

export const optionToValue = (o: DropdownOption) => o.value

export const isEqualTo = (options1: DropdownOption[], options2: DropdownOption[]): boolean => {
  if (options1.length !== options2.length) return false
  const values1 = options1.map(optionToValue).sort()
  const values2 = options2.map(optionToValue).sort()
  return values1.every((value, index) => value === values2[index])
}

export function isNoInformation(selectedOption: DropdownOption) {
  return selectedOption.value === 'NO_INFORMATION'
}
